'use client';

import type { User } from 'firebase/auth';
import { useEffect, useRef, useState } from 'react';
import type { FirstCollectionWorkflow } from '@/lib/first-collection-progress';

type WorkflowStatusResponse = {
  workflow: FirstCollectionWorkflow;
  polling?: {
    suggested_interval_ms?: number;
  };
};

type CachedWorkflowStatus = {
  response: WorkflowStatusResponse;
  fetchedAt: number;
};

type FirstCollectionProgressState = {
  requestKey: string | null;
  workflow: FirstCollectionWorkflow | null;
  isLoading: boolean;
  error: string | null;
};

const MIN_POLL_INTERVAL_MS = 5_000;
const MAX_POLL_INTERVAL_MS = 30_000;
const MAX_ERROR_RETRY_MS = 60_000;
const MAX_CACHE_ENTRIES = 50;
const SLOW_POLL_AFTER_UNCHANGED_COUNT = 2;
const IDLE_POLL_AFTER_UNCHANGED_COUNT = 6;
const workflowStatusCache = new Map<string, CachedWorkflowStatus>();
const COMPLETED_WORKFLOW_STORAGE_PREFIX = 'attune:first-collection-complete:';

function hasCompletedWorkflowMarker(requestKey: string): boolean {
  try {
    return (
      window.sessionStorage.getItem(`${COMPLETED_WORKFLOW_STORAGE_PREFIX}${requestKey}`) === '1'
    );
  } catch {
    return false;
  }
}

function markWorkflowComplete(requestKey: string) {
  try {
    window.sessionStorage.setItem(`${COMPLETED_WORKFLOW_STORAGE_PREFIX}${requestKey}`, '1');
  } catch {
    // Storage can be unavailable in privacy-restricted contexts.
  }
}

class WorkflowStatusRequestError extends Error {
  retryable: boolean;

  constructor(message: string, retryable: boolean) {
    super(message);
    this.name = 'WorkflowStatusRequestError';
    this.retryable = retryable;
  }
}

function getPollingInterval(response: WorkflowStatusResponse): number {
  const suggested = response.polling?.suggested_interval_ms;
  if (!Number.isFinite(suggested) || !suggested) return MIN_POLL_INTERVAL_MS;
  return Math.max(MIN_POLL_INTERVAL_MS, Math.min(MAX_POLL_INTERVAL_MS, suggested));
}

function getWorkflowSignature(workflow: FirstCollectionWorkflow): string {
  return [
    workflow.state,
    workflow.currentStage || '',
    workflow.progressPercent,
    ...workflow.stages.map(stage => `${stage.key}:${stage.status}`),
  ].join('|');
}

function getAdaptivePollingInterval(
  response: WorkflowStatusResponse,
  unchangedPollCount: number
): number {
  const baseInterval = getPollingInterval(response);
  if (unchangedPollCount >= IDLE_POLL_AFTER_UNCHANGED_COUNT) {
    return Math.max(baseInterval, MAX_POLL_INTERVAL_MS);
  }
  if (unchangedPollCount >= SLOW_POLL_AFTER_UNCHANGED_COUNT) {
    return Math.max(baseInterval, 15_000);
  }
  return baseInterval;
}

function isTerminal(workflow: FirstCollectionWorkflow | null): boolean {
  return Boolean(workflow?.isComplete || workflow?.hasFailed);
}

function writeWorkflowCache(requestKey: string, entry: CachedWorkflowStatus) {
  if (!workflowStatusCache.has(requestKey) && workflowStatusCache.size >= MAX_CACHE_ENTRIES) {
    const oldestKey = workflowStatusCache.keys().next().value as string | undefined;
    if (oldestKey) workflowStatusCache.delete(oldestKey);
  }
  workflowStatusCache.set(requestKey, entry);
}

async function fetchWorkflowStatus({
  user,
  jobId,
  signal,
}: {
  user: User;
  jobId: string;
  signal: AbortSignal;
}): Promise<WorkflowStatusResponse> {
  const token = await user.getIdToken();
  const response = await fetch(`/api/attune/workflows/${encodeURIComponent(jobId)}?compact=1`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
    signal,
  });

  if (!response.ok) {
    throw new WorkflowStatusRequestError(
      'First collection progress is temporarily unavailable.',
      response.status !== 401 && response.status !== 403
    );
  }

  const payload = (await response.json()) as WorkflowStatusResponse;
  if (!payload.workflow || !Array.isArray(payload.workflow.stages)) {
    throw new WorkflowStatusRequestError(
      'First collection progress is temporarily unavailable.',
      true
    );
  }
  return payload;
}

export function useFirstCollectionProgress({
  user,
  jobId,
}: {
  user: User | null;
  jobId: string | null;
}) {
  const userRef = useRef(user);
  userRef.current = user;

  const userId = user?.uid || null;
  const requestKey = userId && jobId ? `${userId}:${jobId}` : null;
  const [state, setState] = useState<FirstCollectionProgressState>({
    requestKey: null,
    workflow: null,
    isLoading: false,
    error: null,
  });

  useEffect(() => {
    if (!requestKey || !userId || !jobId) {
      setState({ requestKey: null, workflow: null, isLoading: false, error: null });
      return;
    }

    let cancelled = false;
    let requestInFlight = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let activeController: AbortController | null = null;
    let consecutiveErrors = 0;
    let unchangedPollCount = 0;
    let latestWorkflow: FirstCollectionWorkflow | null = null;
    let latestWorkflowSignature: string | null = null;
    let latestPublishedError: string | null = null;
    let fatalError = false;
    const cached = workflowStatusCache.get(requestKey);
    if (hasCompletedWorkflowMarker(requestKey)) {
      setState({ requestKey, workflow: null, isLoading: false, error: null });
      return;
    }

    const clearTimer = () => {
      if (!timer) return;
      clearTimeout(timer);
      timer = null;
    };

    const schedulePoll = (delayMs: number) => {
      clearTimer();
      if (
        cancelled ||
        fatalError ||
        isTerminal(latestWorkflow) ||
        document.visibilityState !== 'visible' ||
        !navigator.onLine
      ) {
        return;
      }
      timer = setTimeout(() => {
        void poll();
      }, delayMs);
    };

    const poll = async () => {
      if (
        cancelled ||
        requestInFlight ||
        fatalError ||
        isTerminal(latestWorkflow) ||
        document.visibilityState !== 'visible' ||
        !navigator.onLine
      ) {
        return;
      }

      const activeUser = userRef.current;
      if (!activeUser || activeUser.uid !== userId) return;

      requestInFlight = true;
      activeController = new AbortController();
      try {
        const response = await fetchWorkflowStatus({
          user: activeUser,
          jobId,
          signal: activeController.signal,
        });
        if (cancelled) return;

        const nextWorkflowSignature = getWorkflowSignature(response.workflow);
        const workflowChanged = nextWorkflowSignature !== latestWorkflowSignature;
        unchangedPollCount = workflowChanged ? 0 : unchangedPollCount + 1;
        latestWorkflowSignature = nextWorkflowSignature;
        latestWorkflow = response.workflow;
        consecutiveErrors = 0;
        writeWorkflowCache(requestKey, { response, fetchedAt: Date.now() });
        if (response.workflow.isComplete) markWorkflowComplete(requestKey);
        if (workflowChanged || latestPublishedError) {
          latestPublishedError = null;
          setState({
            requestKey,
            workflow: response.workflow,
            isLoading: false,
            error: null,
          });
        }

        if (!isTerminal(response.workflow)) {
          schedulePoll(getAdaptivePollingInterval(response, unchangedPollCount));
        }
      } catch (error) {
        if (cancelled || (error instanceof Error && error.name === 'AbortError')) return;

        consecutiveErrors += 1;
        fatalError = error instanceof WorkflowStatusRequestError && !error.retryable;
        const nextError =
          error instanceof Error
            ? error.message
            : 'First collection progress is temporarily unavailable.';
        if (nextError !== latestPublishedError) {
          latestPublishedError = nextError;
          setState({
            requestKey,
            workflow: latestWorkflow,
            isLoading: false,
            error: nextError,
          });
        }

        if (!fatalError) {
          schedulePoll(
            Math.min(MAX_ERROR_RETRY_MS, MIN_POLL_INTERVAL_MS * 2 ** Math.min(consecutiveErrors, 4))
          );
        }
      } finally {
        requestInFlight = false;
        activeController = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        clearTimer();
        return;
      }
      if (!isTerminal(latestWorkflow) && !fatalError) {
        clearTimer();
        void poll();
      }
    };
    const handleOnline = () => {
      if (!isTerminal(latestWorkflow) && !fatalError) {
        clearTimer();
        void poll();
      }
    };

    if (cached) {
      latestWorkflow = cached.response.workflow;
      latestWorkflowSignature = getWorkflowSignature(cached.response.workflow);
      latestPublishedError = null;
      setState({
        requestKey,
        workflow: cached.response.workflow,
        isLoading: false,
        error: null,
      });
    } else {
      setState({ requestKey, workflow: null, isLoading: true, error: null });
    }

    if (cached && isTerminal(cached.response.workflow)) {
      return;
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    if (cached) {
      const interval = getPollingInterval(cached.response);
      const remainingDelay = Math.max(0, interval - (Date.now() - cached.fetchedAt));
      schedulePoll(remainingDelay);
    } else {
      void poll();
    }

    return () => {
      cancelled = true;
      clearTimer();
      activeController?.abort();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [jobId, requestKey, userId]);

  if (state.requestKey !== requestKey) {
    return {
      workflow: null,
      isLoading: Boolean(requestKey),
      error: null,
    };
  }

  return {
    workflow: state.workflow,
    isLoading: state.isLoading,
    error: state.error,
  };
}
